import type { CustomerOrderTask as OrderTask } from '@einwert/valuation-api-client'
import { useGetOrderOverviewByOrderId } from 'ClientPortal/src/api/Queries/orders'
import { useGetSingleAssetValueOverview } from 'ClientPortal/src/api/Queries/value-overview'
import { useGetTaskChangeLog } from 'ClientPortal/src/api/Queries/tasks'

import { findLast, orderBy } from 'lodash'
import { useTaskDetails } from 'ClientPortal/src/pages/SideDrawers/useTaskDetails'
import invariant from 'tiny-invariant'
import { DateString } from 'ComponentLibrary/src/common/date'
import { User } from 'ComponentLibrary'
import { UUID } from 'ComponentLibrary/src/common/uuid'
import { throwOnForbiddenApiError } from 'ClientPortal/src/api/react-query-config'

export function useGetOrderWithAssetOfFinalisedValuation(orderId: UUID | undefined) {
    const { orderOverview, isOrderOverviewPending } = useGetOrderOverviewByOrderId(orderId)
    const { singleAssetValueOverview, isSingleAssetValueOverviewPending } = useGetSingleAssetValueOverview({
        assetId: orderOverview?.valuation_status === 'finalised' ? orderOverview?.asset_uuid : undefined,
        throwOnError: throwOnForbiddenApiError,
    })

    return { orderOverview, isOrderOverviewPending, singleAssetValueOverview, isSingleAssetValueOverviewPending }
}

export type TaskLogSimplified = { author_user?: Pick<User, 'email' | 'first_name' | 'last_name'>; timestamp: DateString }

function getTaskCreatedLogIfExists(taskData: OrderTask): TaskLogSimplified {
    return { timestamp: taskData.created_on, author_user: taskData.created_by }
}

export function useTaskChangeLogCreatedLastClosed(taskId: string): {
    taskCreatedLog?: TaskLogSimplified
    taskLastClosedLog?: TaskLogSimplified
    isPending: boolean
} {
    const { taskChangeLogData, isTaskChangeLogPending } = useGetTaskChangeLog(taskId, { throwOnError: throwOnForbiddenApiError })
    const { taskData, isPending: isTaskPending } = useTaskDetails({ taskId })

    const isPending = isTaskChangeLogPending || isTaskPending
    if (isPending) {
        return { isPending }
    }

    invariant(taskData, 'taskData must be defined')
    invariant(taskChangeLogData, 'taskChangeLogData must be defined')

    if (taskData.status !== 'done') {
        return {
            taskCreatedLog: getTaskCreatedLogIfExists(taskData),
            isPending: false,
        }
    }

    const orderedChangeLog = orderBy(taskChangeLogData.changes, (changeLogItem) => new Date(changeLogItem.timestamp), ['asc'])
    const taskLastClosedLog = findLast(
        orderedChangeLog,
        (changeLogItem) => changeLogItem.changed_property === 'status' && changeLogItem.new === 'ValuationTaskStatus.DONE'
    )

    return {
        taskCreatedLog: getTaskCreatedLogIfExists(taskData),
        taskLastClosedLog: taskLastClosedLog ?? getTaskCreatedLogIfExists(taskData),
        isPending: false,
    }
}
